import { useEffect } from 'react';
import { useGetProjectsQuery } from 'app/services/projects';
import { IClientBusiness, IProject } from 'app/services/responseTypes';
import { ITask, useGetTasktByFilterIdQuery } from 'app/services/tasks';
import {
  IUser,
  useGetAdminUserBusinessByIdQuery,
  useLazyGetMinimalClientsQuery,
  UserRoleEnum,
} from 'app/services/user';
import Label from 'Components/Form/Label';
import MultiSelectSearchDropDown from 'Components/Form/SearchDropDown/MultiSelectSearchDropDown';
import SelectSearchDropDown from 'Components/Form/SearchDropDown/SelectSearchDropDown';
import Pills from 'Components/Pills';
import SingleUserSelector from 'Components/Selectors/SingleUserSelector';
import { useAuth } from 'utils/AuthProvider';

export function ClientBusinessTaskTaxReturnSelect({
  clientId: _clientId,
  businessId,
  taskId,
  projectId,
  projects,
  lockClient,
  lockProject,
  lockBusiness,
  lockTask,
  onChange,
  excludeClientIds = [],
  withProjectSelector = true,
  withTaskSelector = true,
  withBusinessSelector = true,
  withMultiProjectSelector = false,
}: {
  clientId?: number;
  businessId?: number;
  taskId?: number;
  excludeClientIds?: number[];
  projectId?: number;
  projects?: IProject[];
  lockClient?: IUser | false;
  lockTask?: ITask | false;
  lockProject?: IProject | false;
  lockBusiness?: IClientBusiness | false;
  withProjectSelector?: boolean;
  withTaskSelector?: boolean;
  withBusinessSelector?: boolean;
  withMultiProjectSelector?: boolean;
  onChange: (v: {
    clientId?: number;
    businessId?: number;
    taskId?: number;
    projectId?: number;
    projects?: IProject[];
  }) => void;
}) {
  const { isClient, userId: currentLoggedInUserId } = useAuth();

  const clientId = isClient ? currentLoggedInUserId : _clientId;

  const [getClients, { data: users, isFetching }] =
    useLazyGetMinimalClientsQuery();

  useEffect(() => {
    if (!isClient && !lockClient) {
      getClients();
    }
  }, [isClient, lockClient, getClients]);

  const { data: businesses, isFetching: isLoadingBusinesses } =
    useGetAdminUserBusinessByIdQuery(clientId!, {
      skip: !clientId || !withBusinessSelector || !!lockBusiness,
    });

  const { data: tasks, isFetching: isLoadingTasks } =
    useGetTasktByFilterIdQuery(
      {
        business_id: businessId,
        client_id: clientId,
      },
      { skip: !clientId || !withTaskSelector || !!lockTask },
    );

  const { data: paginatedProjects, isFetching: isloadingProjects } =
    useGetProjectsQuery(
      {
        filters: { business_id: businessId, client_id: clientId },
        per_page: 20,
      },
      { skip: !clientId || !withProjectSelector },
    );

  return (
    <>
      {!isClient && (
        <>
          <Label label="Client" />
          <SingleUserSelector
            userId={clientId}
            disabled={!!lockClient || isClient}
            options={lockClient ? [lockClient] : users}
            isLoading={lockClient ? false : isFetching}
            exculde={excludeClientIds}
            onChangeId={(clientId) =>
              onChange({
                clientId,
                businessId: undefined,
                taskId: undefined,
                projectId: undefined,
              })
            }
          />
        </>
      )}

      {clientId && withBusinessSelector && !lockTask && !lockProject && (
        <>
          <Label label="Business" />
          <SelectSearchDropDown
            valueId={businessId}
            disabled={!!lockTask || !!lockProject || !!lockBusiness}
            options={lockBusiness ? [lockBusiness] : businesses}
            isLoading={lockBusiness ? false : isLoadingBusinesses}
            optionItemSkin={(o) => o.name}
            selectedItemSkin={(o) => o.name}
            clearable={true}
            searchFilter={(s, options) =>
              options.filter((o) => o.name.includes(s))
            }
            searchable={true}
            onChangeId={(businessId) =>
              onChange({
                clientId,
                businessId,
                taskId: undefined,
                projectId: undefined,
              })
            }
          />
        </>
      )}

      {clientId && !lockProject && withTaskSelector && (
        <>
          <Label label="Task" />
          <SelectSearchDropDown
            valueId={taskId}
            disabled={!!lockTask}
            options={lockTask ? [lockTask] : tasks}
            isLoading={lockTask ? false : isLoadingTasks}
            optionItemSkin={(o) => o.title}
            selectedItemSkin={(o) => o.title}
            clearable={true}
            searchable={true}
            searchFilter={(s, options) =>
              options.filter((o) => o.title.includes(s))
            }
            onChangeId={(taskId) =>
              onChange({
                clientId,
                businessId,
                taskId,
                projectId: undefined,
              })
            }
          />
        </>
      )}

      {clientId && !lockTask && withProjectSelector && (
        <>
          <Label label="Tax Return" />
          <SelectSearchDropDown
            valueId={projectId}
            disabled={!!lockProject}
            options={lockProject ? [lockProject] : paginatedProjects?.data}
            isLoading={lockProject ? false : isloadingProjects}
            optionItemSkin={(o) => o.title}
            selectedItemSkin={(o) => o.title}
            clearable={true}
            searchable={true}
            searchFilter={(s, options) =>
              options.filter((o) => o.title.includes(s))
            }
            onChangeId={(projectId) =>
              onChange({
                clientId,
                businessId,
                taskId: undefined,
                projectId,
              })
            }
          />
        </>
      )}

      {clientId && !lockTask && withMultiProjectSelector && (
        <>
          <Label label="Tax Returns" />
          <MultiSelectSearchDropDown
            value={projects || []}
            options={paginatedProjects?.data || []}
            optionItemSkin={(o) => o.title}
            selectedItemSkin={(o) => <Pills.Delete>{o.title}</Pills.Delete>}
            searchFilter={(s, options) =>
              options.filter((o) => o.title.includes(s))
            }
            onChange={(projects) =>
              onChange({
                clientId,
                businessId,
                taskId: undefined,
                projectId: undefined,
                projects: projects || [],
              })
            }
          />
        </>
      )}
    </>
  );
}
